import { createClient } from '@/lib/supabase/client';

const EDGE_FUNCTION_URL = `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/send-email`;

async function invokeEmailFunction(payload: Record<string, unknown>) {
  const supabase = createClient();
  const { data: { session } } = await supabase.auth.getSession();

  const res = await fetch(EDGE_FUNCTION_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session?.access_token ?? process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify(payload),
  });

  const result = await res.json();
  if (!res.ok) throw new Error(result?.error ?? 'Failed to send email');
  return result;
}

/**
 * Send a password reset confirmation email.
 * Call this AFTER Supabase has sent the reset link — use it to send a branded confirmation.
 */
export async function sendPasswordResetEmail(params: {
  to: string;
  userName?: string;
  resetLink: string;
}) {
  return invokeEmailFunction({
    type: 'password_reset',
    to: params.to,
    data: {
      userName: params.userName,
      resetLink: params.resetLink,
    },
  });
}

/**
 * Send a team invite email to a new team member.
 */
export async function sendTeamInviteEmail(params: {
  to: string;
  inviteeName?: string;
  inviterName?: string;
  role?: string;
  companyName?: string;
  inviteLink: string;
}) {
  return invokeEmailFunction({
    type: 'team_invite',
    to: params.to,
    data: {
      inviteeName: params.inviteeName,
      inviterName: params.inviterName,
      role: params.role,
      companyName: params.companyName,
      inviteLink: params.inviteLink,
    },
  });
}

/**
 * Send an overdue job alert email to a manager/admin.
 */
export async function sendOverdueJobAlertEmail(params: {
  to: string | string[];
  woNumber: string;
  woTitle: string;
  customer: string;
  engineer: string;
  deadline: string;
  daysOverdue: number;
  woLink?: string;
}) {
  return invokeEmailFunction({
    type: 'overdue_job_alert',
    to: params.to,
    data: {
      woNumber: params.woNumber,
      woTitle: params.woTitle,
      customer: params.customer,
      engineer: params.engineer,
      deadline: params.deadline,
      daysOverdue: params.daysOverdue,
      woLink: params.woLink ?? `${process.env.NEXT_PUBLIC_SITE_URL}/works-order-management`,
    },
  });
}

/**
 * Send a welcome email after onboarding completes.
 * Confirms company name, role, and dashboard access link.
 */
export async function sendWelcomeOnboardingEmail(params: {
  to: string;
  userName?: string;
  companyName: string;
  role: string;
  dashboardLink?: string;
}) {
  return invokeEmailFunction({
    type: 'welcome_onboarding',
    to: params.to,
    data: {
      userName: params.userName,
      companyName: params.companyName,
      role: params.role,
      dashboardLink: params.dashboardLink ?? `${process.env.NEXT_PUBLIC_SITE_URL ?? 'https://enginework7815.builtwithrocket.new'}`,
    },
  });
}

/**
 * Send a platform-level invite email (sent by super-admins to onboard new companies).
 */
export async function sendPlatformInviteEmail(params: {
  to: string;
  companyName?: string;
  message?: string;
  signupLink?: string;
}) {
  return invokeEmailFunction({
    type: 'platform_invite',
    to: params.to,
    data: {
      companyName: params.companyName,
      message: params.message,
      signupLink: params.signupLink ?? `${process.env.NEXT_PUBLIC_SITE_URL ?? 'https://enginework7815.builtwithrocket.new'}/sign-up-login`,
    },
  });
}
